import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { unwrap } from '../utils/api';
import './ImageGallery.css';

// Six-image grid on the home page. One template per top-level category, the
// first six by admin orderIndex, excluding the "It's something else" catch-
// all. For every category we pool its own products with all of its
// sub-categories' products (so e.g. Shidduch — which usually has no direct
// products, only Tenoim / Wedding / Bavarfen / Vort children — still gets
// represented).
//
// The chosen template per category rotates on a 2-hour cycle: a deterministic
// hash of (windowIndex, categoryId) picks the pool index, so every visitor in
// the same 2-hour window sees the same set. Tabs left open also rotate live —
// a timer wakes the effect at the next window boundary.

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

function stableHash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h;
}

export default function ImageGallery() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rotationTick, setRotationTick] = useState(0);
  const navigate = useNavigate();

  // Fetch + assign on mount, and again whenever the 2-hour boundary fires
  // a tick. Effect depends on rotationTick so a state bump re-runs it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get('/categories/GetAllCategoriesAndSubCategories');
        const tree = unwrap(r)?.categories || [];
        const picked = tree
          .filter(c => !/something/i.test(c.friendlyUrlName || c.name || ''))
          .slice(0, 6);

        // For each top-level category, pool products from itself AND each
        // sub-category. GetProductsByCategoryFriendlyName only matches the
        // exact slug, so sub-cat products won't appear if we query just
        // the parent. Shidduch is the obvious case: most of its templates
        // live under Tenoim / Wedding / Bavarfen.
        const pools = await Promise.all(
          picked.map(async (cat) => {
            const slugs = [
              cat.friendlyUrlName,
              ...((cat.subCategories || []).map(s => s.friendlyUrlName)),
            ].filter(Boolean);
            const lists = await Promise.all(
              slugs.map(slug =>
                api.get(`/products/GetProductsByCategoryFriendlyName/${slug}`)
                  .then(r => unwrap(r) || [])
                  .catch(() => [])
              )
            );
            // Flatten + de-dup (a product in multiple categories could
            // appear twice if it overlaps).
            const seen = new Set();
            const pool = [];
            for (const p of lists.flat()) {
              const id = String(p._id);
              if (seen.has(id)) continue;
              seen.add(id);
              pool.push(p);
            }
            return pool;
          })
        );

        const windowKey = Math.floor(Date.now() / TWO_HOURS_MS);
        const chosen = picked
          .map((cat, i) => {
            const pool = pools[i];
            if (!pool.length) return null;
            const idx = stableHash(`${windowKey}-${cat._id}`) % pool.length;
            return { category: cat, product: pool[idx] };
          })
          .filter(Boolean);

        if (!cancelled) setTemplates(chosen);
      } catch (_) {
        if (!cancelled) setTemplates([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [rotationTick]);

  // Schedule a state bump at the next 2-hour boundary so tabs left open
  // also rotate. After firing, the new effect schedules the next boundary.
  useEffect(() => {
    const nextBoundary = (Math.floor(Date.now() / TWO_HOURS_MS) + 1) * TWO_HOURS_MS;
    const ms = Math.max(1000, nextBoundary - Date.now() + 500); // +500ms safety
    const t = setTimeout(() => setRotationTick(k => k + 1), ms);
    return () => clearTimeout(t);
  }, [rotationTick]);

  const handleTemplateClick = (productId) => {
    navigate(`/card/${productId}`);
  };

  return (
    <section className="gallery-section">
      <div className="gallery-header">
        <h2>Our Latest Creations</h2>
        <p>A visual collection of our most recent works – each piece crafted with intention, emotion, and style.</p>
      </div>

      <div className="gallery-container">
        {loading ? (
          <p style={{ gridColumn: '1/-1', textAlign: 'center', padding: '40px', color: '#888' }}>
            Loading templates…
          </p>
        ) : templates.length > 0 ? (
          templates.map(({ category, product }) => (
            <div
              key={`${category._id}-${product._id}`}
              className="gallery-item"
              onClick={() => handleTemplateClick(product._id)}
              title={`${category.name} · ${product.name}`}
            >
              <img
                src={product.thumbnailWithPlaceholders || product.filesId?.thumbnail || product.filesId?.pathWithWatermark}
                alt={`${category.name} — ${product.name}`}
              />
            </div>
          ))
        ) : (
          <p style={{ gridColumn: '1/-1', textAlign: 'center', padding: '40px', color: '#888' }}>
            No templates available yet.
          </p>
        )}
      </div>
    </section>
  );
}
